import {ToastType} from 'common/components/toast/types';
import {ToggleOption} from 'common/components/toggle/types';

export enum ResourceType {
  Event = `event`,
  User = `user`,
  Cohort = `cohort`,
  Other = `other`,
}

/** List of project ids for internal testing and dogfooding (e.g., api migrations, enabling features) */
export const INTERNAL_PROJECTS = new Set([3, 4, 5, 132990, 1297132]);

export const DEFAULT_CHART_HEIGHT = 600;
export const DEFAULT_CHART_WIDTH = 800;

// amount to time to show the completed, or failed toast before closing the original downloading toast
export const DOWNLOAD_TOAST_OVERLAP_MS = 1500;

export const DOWNLOAD_SUCCESS_TOAST = {
  type: ToastType.SUCCESS,
  message: `Export Complete`,
};

export const DOWNLOAD_FAIL_TOAST = {
  type: ToastType.ERROR,
  message: `Export Failed`,
};

export const LIMITED_CONCURRENCY_PROJECTS = new Set([
  `2087667`, // uber
  `1903813`, // zee5
  `467873`, // picpay
  `1993867`, // apna-time
]);

export const VERY_LIMITED_CONCURRENCY_PROJECTS = new Set([
  `1155510`, // meesho
]);

export enum Loading {
  Bookmarks = `bookmarks`,
  BookmarkedReport = `bookmarkedReport`,
  Download = `download`,
  Result = `result`,
}

export enum ChartVizType {
  Bar = `Bar`,
  Line = `Line`,
  Metric = `Metric`,
  Steps = `Steps`,
  Sankey = `Sankey`,
}

export enum ChartType {
  // insights
  Bar = `bar`,
  Line = `line`,
  Pie = `pie`,
  StackedBar = `bar-stacked`,
  StackedLine = `stacked-line`,
  Table = `table`,
  Metric = `insights-metric`,

  // legacy dashboards-only metric. only referenced due to being saved on the backend
  LegacyMetric = `metric`,

  // flows
  Sankey = `sankey`,
  Flows = `flows`, // alias for Sankey
  FlowsTopPaths = `paths`,

  // funnels
  FunnelTopPaths = `funnel-top-paths`,
  FunnelSteps = `funnel-steps`,
  FunnelStepsMetric = `funnel-steps-metric`,
  FunnelTrend = `funnel-trend`,
  FunnelFrequencyLine = `funnel-frequency-line`,
  FunnelFrequencyBar = `funnel-frequency-bar`,
  FunnelTTCLine = `funnel-ttc-line`,
  FunnelTTCBar = `funnel-ttc-bar`,
  FunnelMedianTTC = `funnel-median-ttc`,

  // retention
  /** Deprecated chart type for retention curve */
  RetentionLine = `line-retention`,
  RetentionTrend = `retention-trend`,
  RetentionTrendMetric = `retention-trend-metric`,
  /** AKA retention curve */
  RetentionTable = `retention-table`,
  Frequency = `frequency`,

  // impact
  ImpactAdoption = `impact-adoption`,
  ImpactTrends = `impact-trends`,
  ImpactPropensity = `impact-propensity`,
}

export const ChartTypeDisplayName: Partial<{[key in ChartType]: string}> = {
  [ChartType.Bar]: `Bar`,
  [ChartType.Line]: `Line`,
  [ChartType.Pie]: `Pie`,
  [ChartType.Metric]: `Metric`,
  [ChartType.StackedBar]: `Stacked Bar`,
  [ChartType.StackedLine]: `Stacked Line`,
  [ChartType.Sankey]: `Top Paths`,
  [ChartType.Table]: `Table`,
  [ChartType.FunnelSteps]: `Funnel Steps`,
  [ChartType.FunnelTrend]: `Funnel Trends`,
  [ChartType.FunnelFrequencyLine]: `Funnel Frequency Line`,
  [ChartType.FunnelFrequencyBar]: `Funnel Frequency Bar`,
  [ChartType.FunnelTTCBar]: `Funnel Time To Convert Bar`,
  [ChartType.FunnelTTCLine]: `Funnel Time To Convert Line`,
  [ChartType.FunnelMedianTTC]: `Time to Convert`,
  [ChartType.RetentionLine]: `Line`,
  // TODO: remove this after retention-consistency work
  [ChartType.RetentionTrend]: `Retention Trends`,
  [ChartType.RetentionTable]: `Retention Curve`,
  [ChartType.Frequency]: `Frequency`,
  [ChartType.FunnelTopPaths]: `Top Paths`,
  [ChartType.FlowsTopPaths]: `Top Paths`,
  [ChartType.Flows]: `User Flows`,
  [ChartType.ImpactAdoption]: `Adoption Over Time`,
  [ChartType.ImpactTrends]: `Impact Trends`,
  [ChartType.ImpactPropensity]: `Causal Impact`,
};

/** Relative or absolute */
export enum ValueRepresentationType {
  Absolute = `absolute`,
  Relative = `relative`,
}

export const ValueRepresentationTypeSelection: {[key in ValueRepresentationType]: ToggleOption} = {
  [ValueRepresentationType.Absolute]: {
    icon: `value-absolute`,
    label: ``,
    value: ValueRepresentationType.Absolute,
    tooltipLabel: `Absolute counts total amounts.`,
  },
  [ValueRepresentationType.Relative]: {
    icon: `value-relative`,
    label: ``,
    value: ValueRepresentationType.Relative,
    tooltipLabel: `Relative calculates the percentage of the whole.`,
  },
};

export const SESSION_START_EVENT_NAME = `$session_start`;
export const SESSION_END_EVENT_NAME = `$session_end`;
export const SESSION_EVENT_NAMES = Object.freeze({
  start: SESSION_START_EVENT_NAME,
  end: SESSION_END_EVENT_NAME,
});
export const SESSION_EVENTS = Object.freeze({
  [SESSION_START_EVENT_NAME]: {session_event: `start`},
  [SESSION_END_EVENT_NAME]: {session_event: `end`},
});

export const SPECIAL_EVENTS = {
  ALL_EVENTS: `$all_events`,
  ALL_MTU_EVENTS: `$all_mtu_events`,
  TOP_EVENTS: `$top_events`,
  ANY_EVENT: `$mp_anything_event`,
  SESSION_START: SESSION_START_EVENT_NAME,
  SESSION_END: SESSION_END_EVENT_NAME,
};

export const SPECIAL_ALL_EVENT_VALUES = Object.freeze([
  SPECIAL_EVENTS.ALL_EVENTS,
  SPECIAL_EVENTS.ANY_EVENT,
  SPECIAL_EVENTS.TOP_EVENTS,
  SPECIAL_EVENTS.ALL_MTU_EVENTS,
]);
export const SPECIAL_EVENT_VALUES = Object.freeze(Object.values(SPECIAL_EVENTS));

/* eslint-disable camelcase */
export const EVENTS = {
  [SPECIAL_EVENTS.ALL_EVENTS]: `All Events`,
  [SPECIAL_EVENTS.ANY_EVENT]: `Any event`, // Keep Lowercase e in event to be consistent with current implementation, for now
  [SPECIAL_EVENTS.TOP_EVENTS]: `Your Top Events`,
  [SPECIAL_EVENTS.SESSION_END]: `Session End`,
  [SPECIAL_EVENTS.SESSION_START]: `Session Start`,
  $ae_crashed: `App Crashed`,
  $ae_first_open: `First App Open`,
  $ae_iap: `In-App Purchase`,
  $ae_notif_opened: `Push Notification Opened`,
  $ae_session: `App Session`,
  $ae_updated: `App Updated`,
  $campaign_bounced: `Message Bounced`,
  $campaign_delivery: `Message Sent`,
  $campaign_marked_spam: `Message Marked Spam`,
  $campaign_link_click: `Message Link Clicked`,
  $campaign_open: `Message Opened`,
  $campaign_received: `Message Received`,
  $create_alias: `Create Alias`,
  $experiment_started: `Experiment Started`,
  $identify: `Identify`,
  $journey_entered: `Campaign Entered`,
  $message_suppressed: `Message Suppressed`,
  $mp_web_page_view: `Page View`,
  $show_survey: `Show Survey`,
  $signup: `Signup`,
};
/* eslint-enable camelcase */

/* eslint-disable camelcase */
export const PROPERTIES = {
  $ae_crashed_reason: `Crash Reason`,
  $ae_first_app_open_date: `First App Open Date`,
  $ae_iap_name: `Product Name`,
  $ae_iap_price: `Product Price`,
  $ae_iap_quantity: `Product Quantity`,
  $ae_notif_message: `Message`,
  $ae_session_length: `Session Length`,
  $ae_total_app_session_length: `Total App Session Length`,
  $ae_total_app_sessions: `Total App Sessions`,
  $ae_updated_version: `Version Updated`,
  $android_app_version: `Android App Version`,
  $android_app_version_code: `Android App Version Code`,
  $android_brand: `Android Brand`,
  $android_devices: `Android Devices`,
  $android_lib_version: `Android Lib Version`,
  $android_manufacturer: `$android_manufacturer`,
  $android_model: `Android Model`,
  $android_notification_id: `Android Notification Id`,
  $android_os: `Android OS`,
  $android_os_version: `Android OS Version`,
  $android_push_error: `$android_push_error`,
  $anon_id: `Anonymous ID`,
  $answer_count: `Answer Count`,
  $answers: `$answers`,
  $app_build_number: `App Build Number`,
  $app_release: `App Release`,
  $app_version: `App Version`,
  $app_version_string: `App Version`,
  $bluetooth_enabled: `Bluetooth Enabled`,
  $bluetooth_version: `Bluetooth Version`,
  $bounce_category: `Bounce Category`,
  $bounce_notification: `$bounce_notification`,
  $bounced: `$bounced`,
  $bounced_reason: `$bounced_reason`,
  $brand: `Brand`,
  $browser: `Browser`,
  $browser_version: `Browser Version`,
  $bucket: `Bucket`,
  $button_id: `Button Id`,
  $button_label: `Button Label`,
  $campaigns: `Messages Sent`,
  $cancelled: `$cancelled`,
  $carrier: `Carrier`,
  $city: `City`,
  $country_code: `Country Code`,
  $created: `Created`,
  $current_url: `Current URL`,
  $dataset: `Project Name`,
  $deliveries: `Deliveries`,
  $device: `Device`,
  $device_id: `Device ID`,
  $distinct_id: `Distinct ID`,
  $distinct_id_before_identity: `Distinct ID Before Identity`,
  $duration: `Duration`,
  $duration_s: `Session Duration (Seconds)`,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  $el_attr__href: `$el_attr__href`,
  $el_text: `$el_text`,
  $email: `Email`,
  $event_count: `Session Event Count`,
  $event_name: `Event Name`,
  $events_distinct_id: `$events_distinct_id`,
  $experiment_id: `Experiment ID`,
  $experiments: `Experiments`,
  $failure_description: `Failure Description`,
  $failure_reason: `Failure Reason`,
  $first_name: `First name`,
  $from_binding: `From Binding`,
  $from_preview: `Sent as preview`,
  $geo_source: `Geo Source`,
  $google_play_services: `Google Play Services`,
  $had_persisted_distinct_id: `Had Persisted Distinct Id`,
  $has_nfc: `Has NFC`,
  $has_telephone: `Has Telephone`,
  $histogram: `Distribution`,
  $host: `Host`,
  $identified_id: `Identified ID`,
  $ignore_time: `$ignore_time`,
  $import: `Import`,
  $initial_referrer: `Initial Referrer`,
  $initial_referring_domain: `Initial Referring Domain`,
  $insert_id: `Insert ID`,
  $ios_app_release: `iOS App Release`,
  $ios_app_version: `iOS App Version`,
  $ios_device_model: `iOS Device Model`,
  $ios_devices: `iOS Devices`,
  $ios_ifa: `iOS IFA`,
  $ios_lib_version: `iOS Lib Version`,
  $ios_notification_id: `iOS Notification Id`,
  $ios_version: `iOS Version`,
  $ip: `IP`,
  $ip_hash: `$ip_hash`,
  $is_reshuffled: `$is_reshuffled`,
  $is_sticky: `Is Sticky Notification`,
  $journey_controls: `Campaign Controls`,
  $journey_id: `Campaign`,
  $journeys: `Journeys`,
  $last_login: `Last login`,
  $last_name: `Last Name`,
  $last_seen: `Updated at`,
  $last_successful_email: `$last_successful_email`,
  $lib_version: `Library Version`,
  $manufacturer: `Manufacturer`,
  $marked_spam: `$marked_spam`,
  $message_id: `Message ID`,
  $model: `Model`,
  $mp_api_endpoint: `API Endpoint`,
  $mp_api_timestamp_ms: `API Timestamp`,
  $mp_first_event_time: `First Seen`,
  $mp_inapp_campaigns: `Inapp Campaigns`,
  $mp_message_sends: `$mp_message_sends`,
  $name: `Name`,
  $neighborhood: `$neighborhood`,
  $notifications: `$notifications`,
  $origin_start: `Session Start Event Name`,
  $origin_end: `Session End Event Name`,
  $os: `Operating System`,
  $os_version: `OS Version`,
  $pathname: `Path Name`,
  $phone: `Phone`,
  $predict_event: `Predict event`,
  $predict_grade: `$predict_grade`,
  $predict_score: `$predict_score`,
  $radio: `Radio`,
  $referrer: `Referrer`,
  $referring_domain: `Referring Domain`,
  $region: `Region`,
  $responses: `$responses`,
  $screen_dpi: `Screen DPI`,
  $screen_height: `Screen Height`,
  $screen_width: `Screen Width`,
  $search_engine: `Search Engine`,
  $survey_collections: `$survey_collections`,
  $survey_responses: `$survey_responses`,
  $survey_shown: `Survey Shown`,
  $surveys: `Surveys Received`,
  $swift_lib_version: `Swift Lib Version`,
  $tap_action_type: `Tap Action Type`,
  $tap_action_uri: `Tap Action URI`,
  $tap_target: `Tap Target`,
  $time: `$time`,
  $timezone: `timezone`,
  $title: `title`,
  $transactions: `Transactions`,
  $unsubscribed: `Unsubscribed`,
  $user_id: `User ID`,
  $username: `Username`,
  $variant_id: `Variant`,
  $watch_model: `watch model`,
  $webdashv2: `$webdashv2`,
  $wifi: `Wifi`,
  alias: `alias`,
  bucket: `bucket`,
  button: `button`,
  campaign_id: `Message`,
  current_domain: `Current Domain`,
  current_page_title: `Page Title`,
  current_url_path: `URL Path`,
  current_url_protocol: `URL Protocol`,
  current_url_search: `URL Search`,
  collection_id: `collection ID`,
  distinct_id: `Distinct ID`,
  has_nfc: `has nfc`,
  ip: `ip`,
  message_id: `variant`,
  message_subtype: `message subtype`,
  message_type: `message type`,
  mp_country_code: `Country`,
  mp_device_model: `Device Model`,
  mp_keyword: `search keyword`,
  mp_lib: `mixpanel library`,
  mp_processing_time: `time processed (UTC)`,
  mp_processing_time_ms: `time processed (UTC)`,
  sent_to: `sent to`,
  survey_id: `survey ID`,
  time: `Time`,
  token: `token`,
  utm_campaign: `utm campaign`,
  utm_content: `utm content`,
  utm_medium: `utm medium`,
  utm_source: `utm source`,
  utm_term: `utm term`,

  // the following are no longer used but
  // should be included for historical reasons
  mp_browser: `Browser`,
  mp_page: `Page View`,
  mp_platform: `Platform`,
  mp_referrer: `Referrer`,

  // computed list properties
  $list_sum: `Sum of List`,
  $list_unique: `Distinct Count`,
  $list_length: `Length of List`,
};
/* eslint-enable camelcase */

export const DATASET_PROPERTY_NAME = `$dataset`;

export enum ProjectType {
  Normal = 1,
  Rollup = 3,
}

export const DATASET_MIXPANEL = `$mixpanel`;
export const DATASET_SALESFORCE = `salesforce`;
export const DATASET_ZENDESK = `zendesk`;

export const DATASET_SALESFORCE_CONTACTS = `contacts`;
export const DATASET_SALESFORCE_LEADS = `leads`;

export enum PropertyType {
  Boolean = `boolean`,
  Datetime = `datetime`,
  List = `list`,
  Number = `number`,
  String = `string`,
  Dimension = `dimension`, // property is a dimension join key
  Object = `object`,
  NonProperty = `other`,
  Unknown = `unknown`,
}

export type FullPropertyType = {
  type: PropertyType;
  isList: boolean;
};

export enum ExtendedPropertyType {
  Object = `object`,
  NonProperty = `other`,
  Unknown = `unknown`,
}

export const TYPECAST_PROPERTY_TYPES: PropertyType[] = [
  PropertyType.String,
  PropertyType.Number,
  PropertyType.Boolean,
  PropertyType.Datetime,
];

export const TYPECAST_LIST_PROPERTY_SUBTYPES: PropertyType[] = [...TYPECAST_PROPERTY_TYPES, PropertyType.Object];

export const NUMERIC_PROPERTY_TYPES = [PropertyType.Boolean, PropertyType.Datetime, PropertyType.Number];

export const STRING_TYPECAST_PROPERTIES = [`message_id`, `campaign_id`, `$experiment_id`, `$variant_id`];

export enum MappedPropertyValues {
  Campaigns = `$campaigns`,
  CampaignId = `campaign_id`,
  Dataset = `$dataset`,
  ExperimentId = `$experiment_id`,
  MessageId = `message_id`,
  VariantId = `$variant_id`,
}

// only a subset of operations are valid for per user aggregations, keep this enum separate
export enum PER_USER_AGGREGATIONS {
  TOTAL = `total`,
  AVERAGE = `average`,
  MIN = `min`,
  MAX = `max`,
  UNIQUE_VALUES = `unique_values`,
}

export enum PRIMITIVE_MATH_TYPES {
  TOTAL = `total`,
  UNIQUE = `unique`,
  SESSIONS = `sessions`,
  AVERAGE = `average`,
  MEDIAN = `median`,
  P25 = `p25`,
  P75 = `p75`,
  P90 = `p90`,
  P99 = `p99`,
  MIN = `min`,
  MAX = `max`,
  HISTOGRAM = `histogram`,
  UNIQUE_VALUES = `unique_values`,
  MOST_FREQUENT = `most_frequent`,
  FIRST_VALUE = `first_value`,
  MRR_WITHIN_SLA = `mrr_within_sla`,
  MRR_WITHIN_SLA_WEEKLY = `mrr_within_sla_weekly`,
  MULTI_ATTRIBUTION = `multi_attribution`,
}

export enum NUMERIC_MATH_TYPES {
  P25 = `p25`,
  P75 = `p75`,
  P90 = `p90`,
  P99 = `p99`,
}

export enum XAU_MATH_TYPES {
  DAU = `dau`,
  WAU = `wau`,
  MAU = `mau`,
}

export const EVENT_MATH_LABELS = {
  [PRIMITIVE_MATH_TYPES.TOTAL]: `Total`,
  [PRIMITIVE_MATH_TYPES.UNIQUE]: `Unique`,
  [PRIMITIVE_MATH_TYPES.SESSIONS]: `Sessions`,
  [PRIMITIVE_MATH_TYPES.AVERAGE]: `Average`,
  [PRIMITIVE_MATH_TYPES.MEDIAN]: `Median`,
  [PRIMITIVE_MATH_TYPES.P25]: `P25`,
  [PRIMITIVE_MATH_TYPES.P75]: `P75`,
  [PRIMITIVE_MATH_TYPES.P90]: `P90`,
  [PRIMITIVE_MATH_TYPES.P99]: `P99`,
  [PRIMITIVE_MATH_TYPES.MIN]: `Min`,
  [PRIMITIVE_MATH_TYPES.MAX]: `Max`,
};

export const PROPERTY_MATH_LABELS = {
  ...EVENT_MATH_LABELS,
  TOTAL: `Sum`,
};

export type MATH_TYPE = PRIMITIVE_MATH_TYPES | NUMERIC_MATH_TYPES | XAU_MATH_TYPES | PER_USER_AGGREGATIONS;

export const XAU_ONLY_MATH_TYPES = Object.values(XAU_MATH_TYPES);

export const ALL_MATH_TYPES = {
  ...PRIMITIVE_MATH_TYPES,
  ...NUMERIC_MATH_TYPES,
  ...XAU_MATH_TYPES,
  ...PER_USER_AGGREGATIONS,
};

export const SPECIAL_SERIES_NAMES = {
  $cohort: `Cohort`,
  $event: `Event`,
  $people: `People`,
};

export const FEATURE_GATES_UNLIMITED = 9223372036854776000; // python max int, aka featureGates.unlimited
export const MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT = 365 * 5; // maximum upgradeable historic data days

export const RESOURCE_LABELS = Object.freeze({
  USER: `User`,
  GROUP: `Group`,
  EVENT: `Event`,
});

export enum CLUSTER_NAMES {
  MAIN_CLUSTER_NAME = `mixpanel-prod-1`,
  EU_CLUSTER_NAME = `mixpanel-prod-eu`,
}

export enum CLUSTER_IDS {
  MIXPANEL_PROD_1_ID = 1,
  MIXPANEL_PROD_EU_ID = 5,
}

export const TRACKING_URL_BY_CLUSTER = {
  [CLUSTER_NAMES.MAIN_CLUSTER_NAME]: `https://api.mixpanel.com`,
  [CLUSTER_NAMES.EU_CLUSTER_NAME]: `https://api-eu.mixpanel.com`,
};

export enum PRODUCTION_DOMAINS {
  EU = `eu.mixpanel.com`,
  US = `mixpanel.com`,
}

export const DISABLED_DUE_TO_CLASSIFIED_DATA = `Disabled due to Classified Data`;

export const ERROR_DUE_TO_MISSING_PROEPRTY_MAPPING = `Unable to Display Data`;

export enum DownloadMode {
  None = 0,
  File = 1,
  Slack = 2,
}

/**
 * These correspond to ReportNames in reports/constants.py
 */
export enum ReportName {
  Cohorts = `cohorts`,
  Dashboards = `dashboards`,
  DataAudit = `audit`,
  EntityManagement = `entity-management`,
  Events = `events`,
  EventsSidebar = `events-sidebar`,
  ExperimentReporting = `experiment-reporting`,
  Explore = `explore`,
  Flows = `flows`,
  Funnels = `funnels`,
  Impact = `impact`,
  Insights = `insights`,
  Integrations = `integrations`,
  JqlConsole = `jql-console`,
  Lexicon = `lexicon`,
  Profile = `profile`,
  Retention = `retention`,
  Revenue = `revenue`,
  Segmentation = `segmentation`,
  Settings = `settings`,
  Setup = `setup`,
  Signal = `signal`,
  Transformations = `transformations`,
  UserSettings = `user_settings`,
  Users = `users`,
  Views = `views`,
  Workspaces = `workspaces`,
}

/**
 * Restricted report names slightly differ from ReportName which is based on url part.
 * These correspond to FEATURE_GATES['restricted_reports'] usage in configurations/settings_global.py
 * e.g `jql` instead of `jql-console`
 */
export enum RestrictedReport {
  ABTesting = `ab_testing`,
  Campaign = `campaign`,
  Cohorts = `cohorts`,
  ExperimentReporting = `experiment-reporting`,
  Flows = `flows`,
  Formulas = `formulas`,
  Impact = `impact`,
  JqlConsole = `jql`,
  Messages = `messages`,
  Predict = `predict`,
  Revenue = `revenue`,
  Signal = `signal`,
  VisualJourneys = `visual_journeys`,
}

/*
 * These correspond to FEATURE_GATES['restrict_entity_saving'] usage in configurations/settings_global.py
 */
export enum RestrictEntitySaving {
  CustomProperties = `custom_properties`,
  Cohorts = `cohorts`,
  LookupTables = `lookup_tables`,
}

/**
 * These correspond to ProjectFeatureFlag in project/models.py
 * and values from OrganizationFeatureflag in webapp/organization/models.py
 */
export enum ProjectFeatureFlag {
  AnomalyDetection = `anomaly_detection`,
  AnomalyDetectionViz = `anomaly_detection_viz`,
  B2B = `b2b_analytics`,
  B2BAnalytics = `b2b_analytics`,
  B2BCohorts = `b2b_cohorts`,
  BehavioralProperties = `behavioral_properties`,
  BrowsePage = `browse_page`,
  CartAnalysis = `cart_analysis`,
  CleanerUrls = `cleaner_url`,
  Cohorts = `cohorts`,
  CustomBucketsFunnels = `custom_buckets_funnels`,
  CustomBucketsRetention = `custom_buckets_retention`,
  CustomizeGroups = `customize_groups`,
  CustomProperty = `custom_property`,
  CustomAlertsImprove = `custom_alerts_improve`,
  CustomAlertsRCA = `custom_alerts_rca`,
  DashboardManagement = `dashboard_management`,
  DataAuditEventsOnly = `data_audit_events_only`,
  DataClassification = `data_classification`,
  DataManagement = `data_management`,
  DataTableVirtualized = `data_table_virtualized`,
  DashboardsFunnelStepsV2 = `dash_funnel_steps_v2`,
  Dev = `dev`,
  Debug = `debug`,
  DisableTrends = `disable_trends`,
  DefaultGroupedSort = `default_grouped_sort`,
  DimensionPropertyLookupTable = `dim_prop_lookup_table`,
  DisableEntityManagementCohorts = `disable_em_cohorts`,
  DropdownsDataDefs = `dropdowns_data_defs`,
  DropdownsSortedUnique = `dropdowns_sorted_unique`,
  EntityManagementCohorts = `entity_mgmnt_cohorts`,
  ERFImprovements = `erf_improvements`,
  EventsSidebar = `events_sidebar`,
  EventApproval = `event_approval`,
  ExploreNewPage = `explore_new_page`,
  FastEdit = `fast_edit`,
  FastEditDev = `fast_edit_dev`,
  FestiveModeStage = `festive_mode_stage`,
  DashboardGrid = `dashboards_grid`,
  FirstEventTime = `first_event_time`,
  FirstTimeEventFilters = `first_time_event_filter`,
  FlowsMultipleAnchors = `flows_multiple_anchors`,
  FlowsMultipleAnchors2 = `flows_multiple_anchors_2`,
  FlowsNewQueryBuilder = `flows_new_qb`,
  FlowsTimePercentiles = `flows_time_percentiles`,
  FlowsAlignmentToggle = `flows_alignment_toggle`,
  FlowsShowCustomEvents = `flows_show_custom_events`,
  FlowsHideCustomEvents = `flows_hide_custom_events`,
  FlowsSankeyCard = `flows_sankey_card`,
  FlowsLessDefaultSteps = `flows_less_steps`,
  FreeSupportTrial = `free_support_trial`,
  FoundationalTemplates = `foundational_templates`, // org
  FunnelsAggregateCharts = `funnels_aggregate_charts`,
  FunnelsChartToggle = `funnels_chart_toggle`,
  FunnelsFrequencyChart = `funnels_frequency_chart`,
  FunnelsFrequencyConversionRate = `funnels_freq_conv_rate`,
  FunnelsNoQueryBuilder = `no_funnels_query_builder`,
  FunnelsQueryBuilder = `funnels_query_builder`,
  FunnelsSessions = `funnels_sessions`,
  FunnelsTrendTypeView = `funnels_trend_type_view`,
  FunnelsViewAsFlows = `funnels_view_flows`,
  HideExperimentConfidence = `hide_experiment_confidence`,
  HideMenuApps = `hide_menu_apps`,
  HideMenuMessages = `hide_menu_messages`,
  HideReportDataAudit = `hide_data_audit`,
  HideReportJqlConsole = `hide_report_jql_console`,
  HideReportRevenue = `hide_report_revenue`,
  HideReportSegmentation = `hide_report_segmentation`,
  IdManagementUIUpdates = `id_management_ui_updates`,
  IdMergeSimplified = `id_merge_simplified`,
  InlineCohorts = `inline_cohorts`,
  InsightsMetricOverrideDefaultSelected = `insight_met_override_sel`,
  InsightsNewCountMenu = `insights_new_count_menu`,
  InsightsPivotTable = `insights_pivot_table`,
  InsightsTableViewN = `insights_table_view_n`,
  InspectorApp = `inspector_app`,
  IntegrationsDataViews = `integrations_dataviews`,
  LexiconDropMixpanel = `lexicon_drop_mixpanel`,
  LexiconGroupProperties = `lexicon_group_properties`,
  Lift = `lift`, // org
  LiftV1 = `lift_v1`, // org
  LiftV2 = `lift_v2`, // org
  LiveviewNewUI = `liveview_new_ui`,
  LiveviewEventInspector = `liveview_event_inspector`,
  LookupTableSharing = `lookup_table_sharing`,
  MarketingTemplate = `marketing_template`, // org
  MessagesAndExperiments = `messages_and_experiments`,
  MultiAttribution = `multi_touch_attribution`,
  NavRedesignV1 = `nav_redesign_v1`, // project
  NewDatePicker = `new_date_picker`,
  NewDatePresets = `new_date_presets`,
  NewTextEditor = `new_text_editor`,
  NewDropdowns = `new_dropdowns`,
  NewNavigation = `new_navigation`, // org
  NestedDashboards = `nested_dashboards`,
  NotifsCampaigns = `notifs_campaigns`,
  NotifsVisualJourneys = `notifs_visual_journeys`,
  Omnisearch = `omnisearch`,
  PinnedDashboards = `pinned_dashboards`,
  Presence = `presence`,
  PublicDashboards = `public_dashboards`,
  QueryTimezone = `query_timezone`,
  QBMetadataSearch = `qb_metadata_search`,
  QBNewMetricsMode = `qb_new_metrics_mode`,
  QBPerLineProperties = `qb_per_line_properties`,
  QBNewFilters = `qb_new_filters`,
  QBCohorts = `qb_cohorts`,
  NoQBCohorts = `no_qb_cohorts`,
  QBSidebar = `qb_sidebar`,
  QBInlineBehaviors = `qb_inline_behaviors`,
  QTS = `query_time_sampling`,
  QTSByDefault = `qts_by_default`,
  RetentionChartView = `retention_chart_view`,
  ServiceAccountsOptOut = `service_accounts_opt_out`,
  ShowReportDataAudit = `show_data_audit`,
  ShowLast1Day = `show_last_1_day`,
  ShowLast7Days = `show_last_7_days`,
  ShowUberWelcome = `show_uber_welcome`,
  ShowUberWelcomeProd = `show_uber_welcome_prod`,
  SingleSegment = `single_segment`,
  SlackV2 = `slack_v2`,
  SlackDigests = `slack_digests`,
  Sst = `sst`,
  TextCards = `text_cards`,
  TextCardAlignment = `text_card_alignment`,
  TrendDataTable = `trend_data_table`,
  UnlimitedEmailDigests = `unlimited_dash_digests`,
  ValueComparison = `value_comparison`,
  ValueComparisonLegacy = `value_comparison_legacy`,
  ViberIngestionSampling = `viber_ingestion_sampling`,
  WarehouseSources = `warehouse_sources`,
}

/**
 * Defines a list of feature flags to include in tracking
 *
 * @todo [pablo] this is not scalable, do not rely on this
 */
export const TRACKING_FEATURE_FLAGS: ProjectFeatureFlag[] = [
  ProjectFeatureFlag.SingleSegment,
  ProjectFeatureFlag.QBInlineBehaviors,
];

export const SHOW_LAST_X_DAYS_FEATURE_FLAGS: ProjectFeatureFlag[] = [
  ProjectFeatureFlag.ShowLast1Day,
  ProjectFeatureFlag.ShowLast7Days,
];

export const SHOW_LAST_X_DAYS_VALUES = {
  [ProjectFeatureFlag.ShowLast1Day]: 1,
  [ProjectFeatureFlag.ShowLast7Days]: 7,
};

/**
 * These correspond to Whitelist in reports/models.py
 */
export enum UserFeatureFlag {
  Dev = `dev`,
  RollupProjects = `rollup-projects`,
  Sampling = `sampling`,
  FestiveModeStage = `festive-mode-stage`,
  SingleSegment = `single_segment`,
}

/**
 * These correspond to UserFlags.FLAGS in user/models.py
 */
export enum UserFlag {
  ClosedPlatformLearnLink = `CLOSED_PLATFORM_LEARN_LINK`,
  DashboardAddedReportOrSeenTooltip = `DASHBOARD_ADDED_REPORT_OR_SEEN_TOOLTIP`,
  MobileInappCustomIntro = `MOBILE_INAPP_CUSTOM_INTRO`,
  RecurringMessagesModal = `RECURRING_MESSAGES_MODAL`,
  ViewedInsightsCount = `VIEWED_INSIGHTS_COUNT`,
  ViewedQueryConsoleIntro = `VIEWED_QUERY_CONSOLE_INTRO`,
  ViewedRecurBetaModal = `VIEWED_RECUR_BETA_MODAL`,
  ViewedRecurV2BetaModal = `VIEWED_RECUR_V2_BETA_MODAL`,
  ViewedReportCount = `VIEWED_REPORT_COUNT`,
  ViewedFYTModal = `VIEWED_BOARDS_FYT_MODAL`,
  ViewedStartupCreditPromo = `VIEWED_STARTUP_CREDIT_PROMO`,
  ViewedRebrandModal = `VIEWED_REBRAND_MODAL`,

  JourneyTut = `JOURNEY_TUT_DONE`,
  MoveIndivCampaign = `VIEWED_MOVE_INDIV_CAMPAIGN_TOOLTIP`,
  MoveJourneyCampaign = `VIEWED_MOVE_JOURNEY_CAMPAIGN_TOOLTIP`,
  NotificationsTut = `NOTIFICATIONS_3_TUT_DONE`,
  OnboardingModal = `VIEWED_ONBOARDING_MODAL`,
  SMSPricingModal = `SMS_PRICING_MODAL`,
  UnreadIndividualTut = `NOTIFICATIONS_INDIVIDUAL_UNREAD_TUT`,
  UnreadJourneyTut = `NOTIFICATIONS_JOURNEY_UNREAD_TUT`,

  EnableOnboardingChecklist = `ENABLE_ONBOARDING_CHECKLIST`,
  ViewedEventsTab = `VIEWED_EVENTS_TAB`,
  DismissedOnboardingChecklist = `DISMISSED_ONBOARDING_CHECKLIST`,
}

export enum FeatureGate {
  AllowCustomAlerts = `allow_custom_alerts`,
  AllowFormattedExport = `allow_formatted_export`,
  AllowRawDataExport = `allow_raw_data_export`,
  AllowSmartHub = `allow_smart_hub`,
  B2BAnalytics = `b2b_analytics`,
  B2BAnalyticsGroups = `b2b_analytics_groups`,
  CanDropEventsAndProperties = `can_drop_events_and_properties`,
  CanExportCsv = `can_export_csv`,
  CanHideEventsAndProperties = `can_hide_events_and_properties`,
  CanMergeEventsAndProperties = `can_merge_events_and_properties`,
  CanSegmentRetention = `can_segment_retention`,
  CanUseDatawarehouseExport = `can_use_datawarehouse_export`,
  CanUsePlatformGithub = `can_use_platform_github`,
  DataClassification = `data_classification`,
  EventApproval = `event_approval`,
  MaxCustomApps = `max_custom_apps`,
  MaxCustomEvents = `max_custom_events`,
  MaxDashboardDigests = `max_dashboard_digests`,
  MaxDataHistoryDays = `max_data_history_days`,
  MaxInsightsFilters = `max_insights_filters`,
  MaxOrgMembers = `max_org_members`,
  MaxProjectMembers = `max_project_members`,
  MaxProjects = `max_projects`,
  MaxSavedReports = `max_saved_reports`,
  MaxSegmentationFilters = `max_segmentation_filters`,
  OnDemandAnalysis = `on_demand_analysis`,
  RestrictedReports = `restricted_reports`,
  RestrictEntitySaving = `restrict_entity_saving`,
  Sharing = `sharing`,
  SSO = `sso`,
  Workspaces = `workspaces`,
}

/**
 * Features enabled for a certain kind of feature gated plan
 * Corresponds to FEATURE_GATES in configurations/settings_global.py
 */
export interface FeatureGatesValue {
  [FeatureGate.AllowCustomAlerts]: boolean;
  [FeatureGate.AllowFormattedExport]: boolean;
  [FeatureGate.AllowRawDataExport]: boolean;
  [FeatureGate.AllowSmartHub]: boolean;
  [FeatureGate.B2BAnalytics]: boolean;
  [FeatureGate.B2BAnalyticsGroups]: boolean;
  [FeatureGate.CanDropEventsAndProperties]: boolean;
  [FeatureGate.CanExportCsv]: boolean;
  [FeatureGate.CanHideEventsAndProperties]: boolean;
  [FeatureGate.CanMergeEventsAndProperties]: boolean;
  [FeatureGate.CanSegmentRetention]: boolean;
  [FeatureGate.CanUseDatawarehouseExport]: boolean;
  [FeatureGate.CanUsePlatformGithub]: boolean;
  [FeatureGate.DataClassification]: boolean;
  [FeatureGate.EventApproval]: boolean;
  [FeatureGate.MaxCustomApps]: number;
  [FeatureGate.MaxCustomEvents]: number;
  [FeatureGate.MaxDashboardDigests]: number;
  [FeatureGate.MaxDataHistoryDays]: number;
  [FeatureGate.MaxInsightsFilters]: number;
  [FeatureGate.MaxOrgMembers]: number;
  [FeatureGate.MaxProjectMembers]: number;
  [FeatureGate.MaxProjects]: number;
  [FeatureGate.MaxSavedReports]: number;
  [FeatureGate.MaxSegmentationFilters]: number;
  [FeatureGate.OnDemandAnalysis]: boolean;
  [FeatureGate.RestrictEntitySaving]: string[];
  [FeatureGate.RestrictedReports]: string[];
  [FeatureGate.Sharing]: boolean;
  [FeatureGate.SSO]: boolean;
  [FeatureGate.Workspaces]: boolean;
}

/**
 * These correspond to the names in user/permission/config.json
 */
export enum Permission {
  AccessDashboardEmailDigests = `access_dashboard_email_digests`,
  AccessProjectSettings = `access_project_settings`,
  AddMemberToTeam = `add_member_to_team`,
  AddProjectToTeam = `add_project_to_team`,
  AddUser = `add_user`,
  AddUserToProject = `add_user_to_project`,
  ChangeNotificationApiKeys = `change_notification_api_keys`,
  ChangePredictEvent = `change_predict_event`,
  CreateProject = `create_project`,
  CreateServiceAccount = `create_service_account`,
  CreateTeam = `create_team`,
  DataDeletion = `data_deletion`,
  DataRetrieval = `data_retrieval`,
  DeleteMembers = `delete_members`,
  DeleteOrg = `delete_org`,
  DeleteProfileFromExplore = `delete_profile_explore`,
  DeleteProject = `delete_project`,
  DeleteProjectFromTeam = `delete_project_from_team`,
  DeleteServiceAccount = `delete_service_account`,
  DeleteTeam = `delete_team`,
  DeleteUser = `delete_user`,
  DeleteUserFromTeam = `delete_user_from_team`,
  DeleteLookupTable = `delete_lookup_table`,
  DownloadCSVData = `download_csv_data`,
  DropEventsProperties = `drop_events_properties`,
  EditABTesting = `edit_ab_testing`,
  EditCreatedEntityVisibility = `edit_created_entity_visibility`,
  EditEntityCreatedByOthers = `edit_entity_created_by_others`,
  EditEntityVisibility = `edit_entity_visibility`,
  EditIntegrations = `edit_integrations`,
  EditLockedCreatedEntity = `edit_locked_created_entity`,
  EditLockedEntity = `edit_locked_entity`,
  EditNotifications = `edit_notifications`,
  EditProjectAutotrack = `edit_project_autotrack`,
  EditProjectInfo = `edit_project_info`,
  EditProjectSecurity = `edit_project_security`,
  EditSurveys = `edit_surveys`,
  EditUserRoles = `edit_userroles`,
  ExportCohorts = `integrations_export_cohorts`,
  HideEventsProperties = `hide_events_properties`,
  InviteAdmin = `invite_admin`,
  InviteAnalyst = `invite_analyst`,
  InviteConsumer = `invite_consumer`,
  LockCreatedEntity = `lock_created_entity`,
  LockEntity = `lock_entity`,
  ManageSensitiveData = `manage_sensitive_data`,
  ManageWorkspaces = `manage_workspaces`,
  MergeEventsProperties = `merge_events_properties`,
  ModifyGuestAccess = `modify_guest_access`,
  PinDashboards = `pin_dashboards`,
  RequestGdprData = `request_gdpr_data`,
  ResetApiKey = `reset_api_key`,
  ResetProject = `reset_project`,
  TransferProject = `transfer_project`,
  UpdateOrg = `update_org`,
  UpdateOrgBilling = `update_org_billing`,
  UpdateTeam = `update_team`,
  UpdateUser = `update_user`,
  ViewABTesting = `view_ab_testing`,
  ViewBaseReports = `view_base_reports`,
  ViewNotifications = `view_notifications`,
  ViewOrganizationSettings = `view_organization_settings`,
  ViewOrgDataPrivacy = `view_org_data_privacy`,
  ViewOrgProjects = `view_org_projects`,
  ViewOrgTeams = `view_org_teams`,
  ViewFindYourOrg = `manage_discoverability`,
  ViewOrgUsers = `view_org_users`,
  ViewProjectSecret = `view_project_secret`,
  ViewUsageAnalytics = `view_usage_analytics`,
  WriteAnnotations = `write_annotations`,
  WriteAutotrack = `write_autotrack`,
  WriteBookmarks = `write_bookmarks`,
  WriteCodeless = `write_codeless`,
  WriteCohorts = `write_cohorts`,
  WriteCustomAlerts = `write_custom_alerts`,
  WriteCustomEvents = `write_customevents`,
  WriteCustomProperties = `write_custom_properties`,
  WriteDashboards = `write_dashboards`,
  WriteDataDefinitions = `write_data_definitions`,
  WriteDataDefinitionSchema = `write_data_definition_schema`,
  WriteLookupTables = `write_lookup_tables`,
  WriteLookupTablesMapping = `write_lookup_tables_mapping`,
  WriteProfileExplore = `write_profile_explore`,
  WriteTypedBookmarks = `write_typed_bookmarks`,
  WriteWarehouseSources = `write_warehouse_sources`,
}

/**
 * For non-PII use, add to STORAGE_ALLOWLIST in iron/common/gdpr-constants.ts
 */
export enum LocalStorageKey {
  SearchedTextPersistedState = `$mp_browse_search_text`,
  // Used to persist a partial search state in the full page "Discover" UI
  BrowsePersistedState = `$mp_browse_persisted_state`,
  // used by featuresStore to cache api response
  FeaturesCache = `$mp_features_cache`,
  // for creating messages from explore
  MessageTargetingFromExplore = `$mp_message_targeting_from_explore`,
  // used by Dashboards to remember the user's last opened dashboard ID for any project or workspace
  LastOpenedDashboard = `$mp_last_opened_dashboard`,
  // list of the last 10 dashboards the user viewed. Used to show the "Recent" sidenav section in a A/B test. If the section persists, this key could be merged with "LastOpenedDashboard".
  RecentlyViewedDashboards = `$mp_recently_viewed_dashboards`,
  // partial of mp-sidenav state to persist display settings e.g. expanded/collapsed, resized width
  Sidenav = `$mp_sidenav`,
  // list of recently pinned dashboards ids that the user already saw. It is used to know if the "New" tag should be displayed
  SeenPinnedDashboardIds = `$mp_seen_pinned_dashboard_ids`,
  // dark mode
  DarkMode = `$mp_dark_mode`,
  // reports panels
  Panel = `$mp_panel`,
  // for persisting latest state in live view
  LastEventInspectorState = `$mp_last_event_inspector_state`,
  // for persisting the type of events/properties menu you are using
  QueryBlockMenuVersion = `$mp_query_block_menu_version`,
  // flag if user has seen the banner for new metric charts
  SeenNewMetricBanner = `$mp_seen_new_metric_banner`,
  // last properties that saved on create/update profile modal
  LastSavedProfileProperties = `$mp_last_saved_properties`,
  // show global filters on dashboards
  ShowDashboardsGlobalFilters = `$mp_dashboards_show_global_filters`,
  // used by currentProjectClient to cache API response
  CurrentProjectClientCache = `$mp_current_project_client_cache`,
  // used by dismiss tooltip tour to store which tour should not displayed again
  DimissTooltipTour = `$mp_dismiss_tooltip_tour`,
  // stores state for Onboarding Checklist
  OnboardingChecklist = `$mp_onboarding_checklist`,
  // list of seen foundational template board ids. Used to know if we should show the feedback toast.
  FoundationalTemplates = `$mp_foundational_templates`,
}

/**
 * Used to delete any user-dependent localStorage data on logout and login.
 * Currently all of the keys in the enum above are user-specific and should be purged,
 * but if we want something to persist across auth and still use the common enum (we should) this should be changed into a subset.
 */
export const USER_DEPENDENT_LOCAL_STORAGE_KEYS: LocalStorageKey[] = Object.values(LocalStorageKey);

/**
 * Help links for basic report introductions
 */
export const REPORT_BASICS_LINKS: Partial<{[key in ReportName]: string}> = {
  [ReportName.Insights]: `https://docs.mixpanel.com/docs/analysis/reports/insights`,
  [ReportName.Funnels]: `https://docs.mixpanel.com/docs/analysis/reports/funnels`,
  [ReportName.Flows]: `https://docs.mixpanel.com/docs/analysis/reports/flows`,
  [ReportName.Retention]: `https://docs.mixpanel.com/docs/analysis/reports/retention`,
  [ReportName.Dashboards]: `https://docs.mixpanel.com/docs/analysis/boards`,
  [ReportName.Impact]: `https://docs.mixpanel.com/docs/analysis/advanced/impact`,
  [ReportName.ExperimentReporting]: `https://docs.mixpanel.com/docs/analysis/advanced/experiments`,
  [ReportName.Signal]: `https://docs.mixpanel.com/docs/analysis/advanced/signal`,
};

export const REPORTS_TO_ADD_DROPDOWN = [
  {
    label: `Insights Report`,
    icon: `brand-insights`,
    value: `insights`,
    href: `insights`,
  },
  {
    label: `Funnels Report`,
    icon: `brand-funnels`,
    value: `funnels`,
    href: `funnels`,
  },
  {
    label: `Flows Report`,
    icon: `brand-flows`,
    value: `flows`,
    href: `flows`,
  },
  {
    label: `Retention Report`,
    icon: `brand-retention`,
    value: `retention`,
    href: `retention`,
  },
];

export const INSIGHTS_LINE_CHART_TYPES = [ChartType.Line, ChartType.StackedLine];
export const INSIGHTS_BAR_CHART_TYPES = [ChartType.Bar, ChartType.StackedBar];

export interface SavedLegendItem {
  key: string;
  color?: number;
}

export interface SavedLegend {
  selected: SavedLegendItem[];
}

// represents a set of individually selected segment combinations
// e.g. Chrome / San Francisco / Insights
export type IndividualSelectedSegments = SavedLegend;

// represents a list of segments to filter at each breakdown level
// e.g. [["Chrome"], ["San Francisco"], ["insights"]]
export type FilteredBreakdownSegments = SavedLegend[];

export const REPORT_LIMITS = {
  [ReportName.Funnels]: {
    csvExport: 10000,
    query: 200,
    segments: {
      [ChartType.FunnelSteps]: {default: 6, max: 12},
      [ChartType.FunnelStepsMetric]: {default: 1, max: 12},
      [ChartType.FunnelTrend]: {default: 6, max: 12},
      [ChartType.FunnelFrequencyLine]: {default: 6, max: 12},
      [ChartType.FunnelFrequencyBar]: {default: 6, max: 12},
      [ChartType.FunnelTTCLine]: {default: 6, max: 12},
      [ChartType.FunnelTTCBar]: {default: 6, max: 12},
      [ChartType.FunnelMedianTTC]: {default: 1, max: 12},
    },
  },
  [ReportName.Insights]: {
    csvExport: 10000,
    query: 3000,
    segments: {
      [ChartType.Line]: {default: 12, max: 50},
      [ChartType.Pie]: {default: 12, max: 50},
      [ChartType.Bar]: {default: 12, max: 300},
      [ChartType.Metric]: {default: 1, max: 12},
    },
  },
  [ReportName.Retention]: {
    csvExport: 1000,
    query: 200,
    segments: {
      [ChartType.RetentionLine]: {default: 6, max: 12},
      [ChartType.RetentionTrend]: {default: 6, max: 12},
      [ChartType.RetentionTrendMetric]: {default: 1, max: 12},
      [ChartType.RetentionTable]: {default: 6, max: 12},
      [ChartType.Frequency]: {default: 6, max: 12},
    },
  },
};

export const RESULTS_OVERALL_SUM_KEY = `$overall`;

export const CONVERSION_WINDOW_UNITS: string[] = [`second`, `minute`, `hour`, `day`, `week`, `month`, `session`];

export const MAX_HOLDING_PROPERTY_CONSTANT = 3;

export const CONVERSION_WINDOW_SIZE_LIMITS = {
  second: {
    MIN: 2,
    MAX: 60 * 60 * 24 * 366,
  },
  minute: {
    MIN: 1,
    MAX: 60 * 24 * 366,
  },
  hour: {
    MIN: 1,
    MAX: 24 * 366,
  },
  day: {
    MIN: 1,
    MAX: 366,
  },
  week: {
    MIN: 1,
    MAX: 52,
  },
  month: {
    MIN: 1,
    MAX: 12, // each month is counted as 30 days
  },
  // Sessions are blocked to 12 at most, since the dqs approx pulls a month of data for each session conv. window.
  // This is an approximation since a user may not have any activity for a year followed by 10 sessions in one day.
  session: {
    MIN: 1,
    MAX: 12,
  },
};

export const HIGHCHARTS_LINKED_ID_SUFFIX = `-$mp-linked`;

export enum TimeBucketType {
  NORMAL = 0,
  LOWER = 1,
  HIGHER = 2,
}
